/**
 * Workspace navigation — the single source for the sidebar and the mobile
 * drawer (handoff §4, design spec §2 "Workspace navigation").
 *
 * Each item keeps the visibility rule the pre-redesign sidebar had
 * (Layout.tsx before U13). Route guards still enforce access; hiding a link
 * is presentation only.
 *
 * Tools opens the workspace tools catalogue (U26); the tools stay listed
 * directly too.
 */
import type { LucideIcon } from 'lucide-react';
import {
    LayoutDashboard, FolderKanban, Activity, CalendarRange, Database, KeyRound,
    ClipboardList, Store, FlaskConical, Users, CreditCard, Building2, Plug, Settings, LayoutGrid, ListTodo, SearchCheck,
} from 'lucide-react';
import { useAuth } from '../../packages/auth-client';
import { useHasPermission } from '../../packages/permission-guard';

export type NavPerm =
    | 'projects:read'
    | 'tasks:read'
    | 'users:read'
    | 'navigation:records'
    | 'navigation:access_requests'
    | 'navigation:templates'
    | 'navigation:marketplace'
    | 'navigation:beta';

export interface NavContext {
    restricted: boolean;
    superAdmin: boolean;
    /** Tenant role "admin". */
    admin: boolean;
    can: (perm: NavPerm) => boolean;
}

export interface NavItem {
    label: string;
    to: string;
    icon: LucideIcon;
    show: (c: NavContext) => boolean;
    /** Defaults to: pathname is `to`'s path or below it. */
    isActive?: (pathname: string, search: URLSearchParams) => boolean;
}

export interface NavGroup {
    id: 'work' | 'tools' | 'manage' | 'account' | 'platform';
    label: string;
    items: NavItem[];
}

// Restricted users get a stripped sidebar: Projects, My work, Reviews and Settings only.
const unrestricted = (c: NavContext) => !c.restricted;
const unrestrictedWith = (perm: NavPerm) => (c: NavContext) => !c.restricted && c.can(perm);
// Platform console: superadmin only, never offered to a tenant admin.
const platform = (c: NavContext) => !c.restricted && c.superAdmin;

// The catalogue is shown when any tool listed beside it is (U26).
const anyTool = (c: NavContext): boolean =>
    NAV.find((g) => g.id === 'tools')!.items.some((i) => i.to !== '/tools' && i.show(c));

export const NAV: NavGroup[] = [
    {
        id: 'work', label: 'Work', items: [
            { label: 'Dashboard', to: '/dashboard', icon: LayoutDashboard, show: unrestricted },
            {
                // Restricted users get Projects unconditionally — it's their only way in.
                label: 'Projects', to: '/projects', icon: FolderKanban,
                show: (c) => c.restricted || c.can('projects:read'),
                isActive: (p) => p.startsWith('/projects') || p.startsWith('/tasks/'),
            },
            // Same rule as Projects: restricted users always get them (the
            // API returns only tasks they may see); others need tasks:read,
            // which GET /me/work and /me/reviews require.
            { label: 'My work', to: '/my-work', icon: ListTodo, show: (c) => c.restricted || c.can('tasks:read') },
            { label: 'Reviews', to: '/reviews', icon: SearchCheck, show: (c) => c.restricted || c.can('tasks:read') },
        ],
    },
    {
        id: 'tools', label: 'Tools', items: [
            { label: 'Workspace tools', to: '/tools', icon: LayoutGrid, show: anyTool },
            { label: 'Workload', to: '/workload', icon: Activity, show: unrestricted },
            { label: 'Timeline', to: '/timeline', icon: CalendarRange, show: unrestricted },
            { label: 'Records', to: '/entities', icon: Database, show: unrestrictedWith('navigation:records') },
            { label: 'Task templates', to: '/templates', icon: ClipboardList, show: unrestrictedWith('navigation:templates') },
            { label: 'Marketplace', to: '/marketplace', icon: Store, show: unrestrictedWith('navigation:marketplace') },
        ],
    },
    {
        id: 'manage', label: 'Manage', items: [
            {
                // Tenant view. A superadmin's /admin/users is the cross-tenant
                // directory in the platform console (U14), so they don't get this link.
                label: 'People & access', to: '/people', icon: Users,
                show: (c) => unrestrictedWith('users:read')(c) && !c.superAdmin,
            },
            { label: 'Access requests', to: '/access-requests', icon: KeyRound, show: unrestrictedWith('navigation:access_requests') },
            { label: 'Connections', to: '/connections', icon: Plug, show: unrestricted },
            {
                // Billing is tenant-admin only server-side (D3): follow the role,
                // not a users:read grant a non-admin can hold.
                label: 'Billing & usage', to: '/billing', icon: CreditCard,
                show: (c) => !c.restricted && (c.superAdmin || c.admin),
                isActive: (p) => p === '/billing', // exact: not /admin/billing/tenants
            },
        ],
    },
    {
        id: 'account', label: 'Account', items: [
            {
                label: 'Settings & help', to: '/settings', icon: Settings, show: () => true,
                isActive: (p) => p === '/settings' || p === '/help' || p.startsWith('/settings/') || p === '/profile',
            },
            { label: 'Beta status', to: '/beta', icon: FlaskConical, show: unrestrictedWith('navigation:beta') },
        ],
    },
    {
        // Deliberate entry into the separate platform shell (PlatformLayout, U14).
        id: 'platform', label: 'Platform console', items: [
            {
                label: 'Open platform console', to: '/admin/overview', icon: Building2, show: platform,
                isActive: () => false,
            },
        ],
    },
];

/** The groups and items this viewer may see; empty groups are dropped. */
export function visibleNav(c: NavContext): NavGroup[] {
    return NAV
        .map((g) => ({ ...g, items: g.items.filter((i) => i.show(c)) }))
        .filter((g) => g.items.length > 0);
}

export function isItemActive(item: NavItem, pathname: string, search: string): boolean {
    if (item.isActive) return item.isActive(pathname, new URLSearchParams(search));
    const path = item.to.split('?')[0];
    return pathname === path || pathname.startsWith(`${path}/`);
}

/** visibleNav for the signed-in viewer. */
export function useNavGroups(): NavGroup[] {
    const { user, isRestricted, isSuperAdmin } = useAuth();
    const granted: Record<NavPerm, boolean> = {
        'projects:read': useHasPermission('projects', 'read'),
        'tasks:read': useHasPermission('tasks', 'read'),
        'users:read': useHasPermission('users', 'read'),
        'navigation:records': useHasPermission('navigation', 'records'),
        'navigation:access_requests': useHasPermission('navigation', 'access_requests'),
        'navigation:templates': useHasPermission('navigation', 'templates'),
        'navigation:marketplace': useHasPermission('navigation', 'marketplace'),
        'navigation:beta': useHasPermission('navigation', 'beta'),
    };
    return visibleNav({
        restricted: isRestricted(),
        superAdmin: isSuperAdmin(),
        admin: (user?.roles ?? []).includes('admin'),
        can: (p) => granted[p],
    });
}
